// Google Analytics Event Tracking Utilities

export const GA_MEASUREMENT_ID = 'G-ZKTQ7DDM7E';

// Helper function to send events to Google Analytics
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

// Page View Tracking (for client-side routing)
export const trackPageView = (path) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
    });
  }
};

// Booking Events
export const trackBookingStart = (engineer, service) => {
  trackEvent('booking_started', {
    event_category: 'Booking',
    event_label: `${engineer} - ${service}`,
    engineer_name: engineer,
    service_type: service,
  });
};

export const trackBookingComplete = (engineer, service, price) => {
  trackEvent('booking_completed', {
    event_category: 'Booking',
    event_label: `${engineer} - ${service}`,
    engineer_name: engineer,
    service_type: service,
    value: price,
    currency: 'USD',
  });
};

export const trackBookingPayment = (bookingId, amount, engineer) => {
  trackEvent('purchase', {
    transaction_id: bookingId,
    value: amount,
    currency: 'USD',
    items: [{
      item_id: bookingId,
      item_name: `Studio Session - ${engineer}`,
      item_category: 'Recording Session',
      price: amount,
      quantity: 1,
    }],
  });
};

// Newsletter Events
export const trackNewsletterSignup = (email) => {
  trackEvent('newsletter_signup', {
    event_category: 'Engagement',
    event_label: 'Newsletter',
    method: 'email',
  });
};

// Internship Events
export const trackInternshipApplication = () => {
  trackEvent('internship_application', {
    event_category: 'Lead Generation',
    event_label: 'Internship Application',
  });
};

// Review Events
export const trackReviewSubmission = (rating) => {
  trackEvent('review_submitted', {
    event_category: 'Engagement',
    event_label: 'Customer Review',
    value: rating,
  });
};

// File Upload Events
export const trackFileUpload = (fileType, fileSize) => {
  trackEvent('file_upload', {
    event_category: 'Engagement',
    event_label: fileType,
    file_type: fileType,
    file_size_mb: (fileSize / (1024 * 1024)).toFixed(2),
  });
};

// Beat Purchase Events
export const trackBeatView = (beatTitle, licenseType) => {
  trackEvent('beat_view', {
    event_category: 'Beats',
    event_label: beatTitle,
    license_type: licenseType,
  });
};

export const trackBeatPurchase = (beatTitle, licenseType, price) => {
  trackEvent('beat_purchase', {
    event_category: 'Beats',
    event_label: `${beatTitle} - ${licenseType}`,
    value: price,
    currency: 'USD',
    license_type: licenseType,
  });
};

// Social Media Events
export const trackSocialClick = (platform) => {
  trackEvent('social_click', {
    event_category: 'Social Media',
    event_label: platform,
    platform: platform,
  });
};

// Contact Events
export const trackContactClick = (method) => {
  trackEvent('contact_click', {
    event_category: 'Contact',
    event_label: method,
    contact_method: method,
  });
};

// Navigation Events
export const trackNavigationClick = (destination) => {
  trackEvent('navigation_click', {
    event_category: 'Navigation',
    event_label: destination,
  });
};

// Bundle/Package Deal Events
export const trackBundleInquiry = (packageName, price) => {
  trackEvent('bundle_inquiry', {
    event_category: 'Packages',
    event_label: packageName,
    package_name: packageName,
    value: price,
    currency: 'USD',
  });
};

// Engineer Profile View
export const trackEngineerView = (engineerName) => {
  trackEvent('engineer_profile_view', {
    event_category: 'Engagement',
    event_label: engineerName,
    engineer_name: engineerName,
  });
};

// Form Interactions
export const trackFormStart = (formName) => {
  trackEvent('form_start', {
    event_category: 'Forms',
    event_label: formName,
    form_name: formName,
  });
};

export const trackFormComplete = (formName) => {
  trackEvent('form_complete', {
    event_category: 'Forms',
    event_label: formName,
    form_name: formName,
  });
};

// Video/Gallery Interactions
export const trackGalleryView = () => {
  trackEvent('gallery_view', {
    event_category: 'Engagement',
    event_label: 'Studio Gallery',
  });
};

export const trackVideoPlay = (videoTitle) => {
  trackEvent('video_play', {
    event_category: 'Media',
    event_label: videoTitle,
  });
};

// Error Tracking
export const trackError = (errorType, errorMessage) => {
  trackEvent('error', {
    event_category: 'Errors',
    event_label: errorType,
    error_message: errorMessage,
  });
};

// PWA Events
export const trackPWAInstallPromptShown = () => {
  trackEvent('pwa_install_prompt_shown', {
    event_category: 'PWA',
    event_label: 'Install Prompt Displayed',
  });
};

export const trackPWAInstallAccepted = () => {
  trackEvent('pwa_install_accepted', {
    event_category: 'PWA',
    event_label: 'App Installed',
    value: 1,
  });
};

export const trackPWAInstallDismissed = () => {
  trackEvent('pwa_install_dismissed', {
    event_category: 'PWA',
    event_label: 'Install Prompt Dismissed',
  });
};

export const trackPWALaunched = () => {
  trackEvent('pwa_launched', {
    event_category: 'PWA',
    event_label: 'App Opened',
  });
};

export const trackPWABooking = (engineer, service, price, hadDiscount) => {
  trackEvent('pwa_booking', {
    event_category: 'PWA',
    event_label: 'Booking Through App',
    engineer_name: engineer,
    service_type: service,
    value: price,
    currency: 'USD',
    discount_applied: hadDiscount,
  });
};

export const trackPWADiscountApplied = (discountAmount, bookingAmount) => {
  trackEvent('pwa_discount_applied', {
    event_category: 'PWA',
    event_label: 'First Booking Discount Used',
    discount_amount: discountAmount,
    booking_amount: bookingAmount,
    currency: 'USD',
  });
};
